import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2,
  ExternalLink,
  Phone,
  Key,
  Hash,
  Webhook,
  Send,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Check,
  Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WhatsAppConfigPanel - Configuration de l'API WhatsApp Cloud (Meta)
 * 
 * Permet de configurer:
 * - Access Token permanent
 * - Phone Number ID
 * - Business Account ID
 * - Webhook (optionnel)
 */
const WhatsAppConfigPanel = ({ config, setConfig, onSave, saving }) => {
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({
    enabled: false,
    access_token: '',
    phone_number_id: '',
    business_account_id: '',
    api_version: 'v22.0',
    webhook_verify_token: '',
    test_phone: ''
  });
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    fetchWhatsAppConfig();
    fetchWhatsAppStatus();
  }, []);

  const fetchWhatsAppConfig = async () => {
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.get(`${API}/system-config/whatsapp`, {});
      
      if (response.data) {
        setWhatsappConfig(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  const fetchWhatsAppStatus = async () => {
    setLoadingStatus(true);
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/whatsapp/status`, {});
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
      setStatus({ enabled: false, error: error.message });
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      await axios.post(`${API}/system-config/whatsapp`, whatsappConfig, {});
      
      toast.success('Configuration WhatsApp enregistrée');
      fetchWhatsAppStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const testConnection = async () => {
    if (!whatsappConfig.test_phone) {
      toast.error('Veuillez entrer un numéro de test');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/notifications/whatsapp/test?phone=${encodeURIComponent(whatsappConfig.test_phone)}`,
        {},
        {}
      );
      
      setTestResult({
        success: response.data.success,
        message: response.data.success 
          ? `Message envoyé avec succès ! ID: ${response.data.message_id}`
          : response.data.error
      });
      
      if (response.data.success) {
        toast.success('Test WhatsApp réussi !');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || error.message
      });
      toast.error('Échec du test WhatsApp');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copié !');
  };

  const generateWebhookToken = () => {
    const token = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
    setWhatsappConfig(prev => ({ ...prev, webhook_verify_token: token }));
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <MessageCircle className="w-5 h-5 text-amber-600 mt-0.5" />
        <div>
          <h4 className="font-semibold text-amber-900">Meta Cloud API — désactivée</h4>
          <p className="text-sm text-amber-700 mt-1">
            L'envoi WhatsApp est exclusivement assuré par <strong>OpenWA</strong> (session QR code, onglet Notifications → WhatsApp).
            Cette configuration Meta Cloud est conservée à titre d'archive et n'est plus utilisée.
          </p>
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Statut de la connexion</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchWhatsAppStatus}>
              <RefreshCw className={`w-4 h-4 ${loadingStatus ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingStatus ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Vérification...
            </div>
          ) : status?.enabled ? (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-green-700 font-medium">Connecté</span>
              <Badge variant="outline" className="ml-2 text-green-700 border-green-300">
                {status.api_version}
              </Badge>
              <Badge variant="outline" className="text-gray-600">
                Phone ID: {status.phone_number_id?.slice(-6)}
              </Badge>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              <span>Non configuré</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Configuration API
          </CardTitle>
          <CardDescription>
            Récupérez ces informations depuis le Meta Business Manager
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-medium">Activer WhatsApp</Label>
              <p className="text-sm text-gray-500 mt-1">
                Activer l'envoi de notifications via WhatsApp
              </p>
            </div>
            <Switch
              checked={whatsappConfig.enabled}
              onCheckedChange={(checked) => setWhatsappConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          <Separator />

          {/* Access Token */}
          <div className="space-y-2">
            <Label htmlFor="access_token" className="flex items-center gap-2">
              <Key className="w-4 h-4 text-gray-500" />
              Access Token permanent *
            </Label>
            <div className="relative">
              <Input
                id="access_token"
                type={showToken ? 'text' : 'password'}
                value={whatsappConfig.access_token}
                onChange={(e) => setWhatsappConfig(prev => ({ ...prev, access_token: e.target.value }))}
                placeholder="EAAG..."
                className="pr-20"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowToken(!showToken)}
                  className="h-7 w-7 p-0"
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Trouvez-le dans Meta Business Suite → WhatsApp → API Setup
            </p>
          </div>

          {/* Phone Number ID */}
          <div className="space-y-2">
            <Label htmlFor="phone_number_id" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              Phone Number ID *
            </Label>
            <Input
              id="phone_number_id"
              value={whatsappConfig.phone_number_id}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, phone_number_id: e.target.value }))}
              placeholder="123456789012345"
            />
            <p className="text-xs text-gray-500">
              ID unique du numéro WhatsApp Business
            </p>
          </div>

          {/* Business Account ID */}
          <div className="space-y-2">
            <Label htmlFor="business_account_id" className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-500" />
              Business Account ID (optionnel)
            </Label>
            <Input
              id="business_account_id"
              value={whatsappConfig.business_account_id}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, business_account_id: e.target.value }))}
              placeholder="123456789012345"
            />
          </div>

          {/* API Version */}
          <div className="space-y-2">
            <Label htmlFor="api_version">Version API</Label>
            <select
              id="api_version"
              value={whatsappConfig.api_version}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, api_version: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="v22.0">v22.0 (Recommandé)</option>
              <option value="v21.0">v21.0</option>
              <option value="v20.0">v20.0</option>
            </select>
          </div>

          <Separator />

          {/* Webhook Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-purple-600" />
              <Label className="text-base font-medium">Webhook (optionnel)</Label>
            </div>
            <p className="text-sm text-gray-500">
              Configurez un webhook pour recevoir les messages entrants et les statuts de livraison.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="webhook_verify_token">Token de vérification Webhook</Label>
              <div className="flex gap-2">
                <Input
                  id="webhook_verify_token"
                  value={whatsappConfig.webhook_verify_token}
                  onChange={(e) => setWhatsappConfig(prev => ({ ...prev, webhook_verify_token: e.target.value }))}
                  placeholder="your_verify_token"
                  className="flex-1"
                />
                <Button variant="outline" onClick={generateWebhookToken}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Générer
                </Button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <Label className="text-sm text-gray-700">URL du Webhook</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-white px-3 py-2 rounded border font-mono">
                  {`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/whatsapp`}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/whatsapp`)}
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Utilisez cette URL dans la configuration Webhook de Meta
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Test de connexion
          </CardTitle>
          <CardDescription>
            Envoyez un message test pour vérifier la configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test_phone">Numéro de test</Label>
            <Input
              id="test_phone"
              value={whatsappConfig.test_phone}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, test_phone: e.target.value }))}
              placeholder="+225 07 XX XX XX XX"
            />
            <p className="text-xs text-gray-500">
              Le numéro doit avoir WhatsApp et être dans une conversation existante (24h)
            </p>
          </div>

          <Button 
            onClick={testConnection} 
            disabled={testing || !whatsappConfig.test_phone}
            className="bg-green-600 hover:bg-green-700"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer un test
          </Button>

          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Test réussi !' : 'Échec du test'}
                  </p>
                  <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Templates */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Info className="w-5 h-5" />
            Templates WhatsApp à configurer sur Meta
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-700 space-y-3">
          <p>
            Pour envoyer des notifications proactives (hors conversation 24h), vous devez créer et faire approuver des templates dans Meta Business Manager.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className="font-medium text-blue-800">Cycle de vie opération :</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2 text-xs">
                <li><code className="bg-blue-100 px-1 rounded">baobab_operation_created</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_devis_ready</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_devis_accepted</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_ready_loading</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_chargement_started</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_en_route</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_delivered</code></li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-medium text-blue-800">Finance & Fin :</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2 text-xs">
                <li><code className="bg-blue-100 px-1 rounded">baobab_payment_pending</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_payment_link</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_payment_received</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_operation_completed</code></li>
                <li><code className="bg-blue-100 px-1 rounded">baobab_operation_cancelled</code></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-2 border-t border-blue-200">
            <p className="text-xs text-blue-600">
              📄 Voir le fichier <code className="bg-blue-100 px-1 rounded">/app/docs/WHATSAPP_TEMPLATES.md</code> pour le contenu détaillé de chaque template et les variables à configurer.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer la configuration
        </Button>
      </div>
    </div>
  );
};

export default WhatsAppConfigPanel;
